/** Number and time formatting, in one place so every page says it the same way. */
import { formatDateTimePref } from "@strat/api";
import { currentDateDisplay } from "@strat/ui/prefs";

/**
 * A percent. A loss uses the same minus sign as a drawdown ("−14.8%", not
 * "-14.8%"), so a return and a drawdown side by side read alike. One that
 * rounds to nothing has no sign.
 */
export function pct(v: number | null | undefined, digits = 1, signed = true): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const abs = Math.abs(v).toFixed(digits);
  if (Number(abs) === 0) return `${abs}%`;
  return `${v < 0 ? "−" : signed ? "+" : ""}${abs}%`;
}

/**
 * A drawdown, always shown as a loss: "−4.26%". One that rounds to nothing
 * reads "0.00%" rather than the "−0.00%" a fresh bot showed.
 */
export function dd(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  return Number(abs.toFixed(digits)) === 0 ? `${(0).toFixed(digits)}%` : `−${abs.toFixed(digits)}%`;
}

export function money(v: number | null | undefined, digits = 0): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function num(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(digits);
}

export function tone(v: number | null | undefined): "up" | "down" | "" {
  if (v == null || !Number.isFinite(v) || v === 0) return "";
  return v > 0 ? "up" : "down";
}

export function timeAgo(when: string | number | Date | null | undefined): string {
  if (when == null) return "—";
  const t = typeof when === "number" ? when : new Date(when).getTime();
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 45) return `${d}d ago`;
  return new Date(t).toLocaleDateString();
}

export function date(when: string | number | Date | null | undefined, withTime = false): string {
  if (when == null) return "—";
  const d = new Date(when);
  // A reader who picked a date order or UTC (Settings → Display) gets it here
  // too; everyone else keeps the friendlier "12 Oct 2026".
  const display = currentDateDisplay();
  if (display.dateFormat !== "dmy" || display.timeZone !== "local") {
    return formatDateTimePref(d.getTime(), display, { time: withTime });
  }
  return withTime
    ? d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
    : d.toLocaleDateString([], { dateStyle: "medium" });
}
